import {Component, EventEmitter, inject, Inject, Input, OnInit, OnDestroy, Output, signal} from '@angular/core';
import {SearchFilterModel} from "../../models/SearchFilter-model";
import {CommonModule} from "@angular/common";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect} from "@angular/material/select";
import {MatAutocomplete, MatAutocompleteModule} from "@angular/material/autocomplete";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {LocalStorageService} from "../../service/LocalStorageService";
import {PureOriginalResultsService} from "../../service/PureOriginalResults.service";
import {VehicleDataService} from "../../service/vehicleData.service";
import {TableSourceEnum, VehicleDataEnum} from "../../models/VehicleData-model";
import {TuningNamesTablesService} from "../../service/Tuning-names-tables.service";
import {debounceTime, distinctUntilChanged, Subject} from 'rxjs';

@Component({
    selector: 'app-most-requested-tuning-filters',
    standalone: true,
    imports: [
        CommonModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatOption,
        MatSelect,
        ReactiveFormsModule,
        MatAutocomplete,
        MatAutocompleteModule
    ],
    templateUrl: './most-requested-tuning-filters.component.html',
    styleUrl: './most-requested-tuning-filters.component.css'
})
export class MostRequestedTuningFiltersComponent implements OnInit, OnDestroy {

    public filteredTuningNames: { [key: string]: string[] } = {};


    public selectedOptions: SearchFilterModel[] = [];
    public tuningNames = signal<string[]>([]);
    public dynamicOptionValues: { [key: string]: string[] } = {};
    private liveSearchSubjectMap: { [key: string]: Subject<string> } = {};
    public vehicleDataService = inject(VehicleDataService);

    @Input() displayedColumns: undefined | string[];
    @Input() persistFilters = true;
    @Output() mostRequestedFilterClosed = new EventEmitter<boolean>();
    @Output() selectedFiltersChange = new EventEmitter<SearchFilterModel[]>();
    @Output() filterCount = new EventEmitter<number>();

    pureOriginalResultsService = inject(PureOriginalResultsService);
    private localStorageService = inject(LocalStorageService);
    private tuningNamesTableService = inject(TuningNamesTablesService);

    searchFilterForm = new FormGroup({});
    readonly TUNING_NAME = 'TUNING_NAME';

    public apiSearchableOptions: string[] = [
        ...Object.values(VehicleDataEnum),
        this.TUNING_NAME // Dodaj kao posebnu opciju
    ].filter(option => option !== VehicleDataEnum.VEHICLE_TYPE);

    public allAvailableOptions: string[] = [];

    private vehicleEnumToInternalKeyMap: Partial<Record<VehicleDataEnum, string>> = {
        [VehicleDataEnum.VEHICLE_PRODUCER]: 'vehicleProducer',
        [VehicleDataEnum.VEHICLE_MODEL]: 'vehicleModel',
        [VehicleDataEnum.VEHICLE_SERIES]: 'vehicleSeries',
        [VehicleDataEnum.ECU_BUILD]: 'ecuBuild',
        [VehicleDataEnum.ECU_PRODUCER]: 'ecuProducer',


    };


    constructor(@Inject('LOCAL_MISSING_KEY') private missingFilter: string,
                @Inject('LOCAL_PARTIAL_KEY') private partialFilter: string) {
    }

    get filterKey(): string {
        return this.pureOriginalResultsService.type() === 'missing'
            ? this.missingFilter
            : this.partialFilter;
    }

    get availableColumns(): string[] {
        if (!this.displayedColumns) return [];
        if (this.pureOriginalResultsService.type() === 'missing') {
            return this.displayedColumns.filter(column => column !== 'hitRate');
        }
        return this.displayedColumns;
    }

    formatEnumKey(key: string): string {
        return key
            .toLowerCase()
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
    }

    getListOfCorrectTuningNames() {
        this.tuningNamesTableService.getListOfCorrectTuningNames().subscribe((response) => {
            this.tuningNames.set(response.correctTuningNames);
        });
    }

    ngOnInit(): void {
        this.tuningNamesTableService.getListOfCorrectTuningNames().subscribe(response => {
            this.tuningNames.set(response.correctTuningNames);
        });

        const excludedKeys = ['vehicleProducer', 'vehicleSeries', 'ecuProducer', 'ecuBuild', 'vehicleModel', 'tuningName'];

        const filteredDisplayedColumns = this.availableColumns
            .filter(option => !excludedKeys.includes(option))
            .filter(option => option !== this.TUNING_NAME);

        const savedFilters = this.pureOriginalResultsService.selectedFilters();
        const savedFilterOptions = savedFilters?.map(f => f.option) || [];

        const missingSavedOptions = savedFilterOptions.filter(
            opt => !Object.values(VehicleDataEnum).includes(opt as VehicleDataEnum) &&
                !filteredDisplayedColumns.includes(opt) &&
                opt !== 'tuningName'
        );

        this.allAvailableOptions = [
            this.TUNING_NAME,
            ...Object.values(VehicleDataEnum).filter(option => option !== VehicleDataEnum.VEHICLE_TYPE),
            ...filteredDisplayedColumns,
            ...missingSavedOptions
        ];

        if (savedFilters && savedFilters.length > 0) {
            const normalizedFilters = savedFilters.map(filter => {
                if (filter.option === 'tuningName') {
                    return { ...filter, option: 'TUNING_NAME' };
                }
                const reverseVehicleMapping: { [key: string]: VehicleDataEnum } = {
                    'vehicleProducer': VehicleDataEnum.VEHICLE_PRODUCER,
                    'vehicleModel': VehicleDataEnum.VEHICLE_MODEL,
                    'vehicleSeries': VehicleDataEnum.VEHICLE_SERIES,
                    'ecuBuild': VehicleDataEnum.ECU_BUILD,
                    'ecuProducer': VehicleDataEnum.ECU_PRODUCER
                };

                const reversedOption = reverseVehicleMapping[filter.option] || filter.option;
                return { ...filter, option: reversedOption };
            });

            this.searchFilterForm = this.localStorageService.createFormGroupFromFilters(
                normalizedFilters,
                this.allAvailableOptions
            );

            Object.keys(this.searchFilterForm.controls).forEach(key => {
                const group = this.searchFilterForm.get(key) as FormGroup;
                const optionControl = group.get('option');
                const valueControl = group.get('value');

                if (optionControl && valueControl) {
                    const currentOption = optionControl.value;
                    if (currentOption === 'hitRate') {
                        valueControl.clearValidators();
                    } else {
                        valueControl.setValidators(Validators.required);
                    }
                    valueControl.updateValueAndValidity();
                }

                this.setupFormGroup(group, key);
            });

            const validFilterCount = this.calculateValidFilterCount();
            this.filterCount.emit(validFilterCount);
        } else {
            this.filterCount.emit(0);
        }
    }


    getFilteredTuningNames(formKey: string, searchValue: string): string[] {
        if (!searchValue || searchValue.trim() === '' || searchValue.trim().length < 1) {
            return []; // Vrati praznu listu umesto sve opcije
        }

        const filtered = this.tuningNames().filter(name =>
            name.toLowerCase().includes(searchValue.toLowerCase())
        );

        this.filteredTuningNames[formKey] = filtered;
        return filtered;
    }



    private setupFormGroup(group: FormGroup, key: string) {
        const optionControl = group.get('option');
        const valueControl = group.get('value');

        if (!optionControl || !valueControl) return;

        optionControl.valueChanges.subscribe(selected => {

            if (selected === 'hitRate') {
                valueControl.clearValidators();
                valueControl.setValue('');
            } else {
                valueControl.setValidators(Validators.required);
            }
            valueControl.updateValueAndValidity();
            valueControl.markAsTouched();
        });

        const subject = new Subject<string>();
        this.liveSearchSubjectMap[key] = subject;

        subject.pipe(debounceTime(300), distinctUntilChanged())
            .subscribe(searchTerm => {
                const selected = optionControl.value;
                const trimmed = (searchTerm || '').trim();

                if (selected === 'TUNING_NAME') {

                    this.getFilteredTuningNames(key, trimmed);

                    if (trimmed.length >= 1) {
                        this.fetchVehicleData('TUNING_NAME', trimmed, key);
                    }

                } else if (this.apiSearchableOptions.includes(selected) && trimmed.length >= 1) {
                    this.fetchVehicleData(selected, searchTerm, key);
                }
            });

        valueControl.valueChanges.subscribe(value => {
            subject.next(value);
        });
    }




    fetchVehicleData(option: string, search: string, formKey: string) {
        let source: TableSourceEnum | 'TUNING_NAMES';

        if (option === 'TUNING_NAME') {
            source = 'TUNING_NAMES';
        } else {
            if (this.pureOriginalResultsService.type() === 'missing') {
                source = TableSourceEnum.PURE_ORIGINAL_MISSING_VERSIONS;
            } else {
                source = TableSourceEnum.PURE_ORIGINAL_MATCHING;
            }
        }

        this.vehicleDataService
            .searchForVehicleData(option as VehicleDataEnum | 'TUNING_NAME', search, source)
            .subscribe({
                next: response => {
                    this.dynamicOptionValues[`${formKey}_${option}`] = response.body || [];
                },
                error: err => console.error('Vehicle data fetch failed', err)
            });
    }




    getDynamicOptions(formKey: string, option: string): string[] {
        return this.dynamicOptionValues[`${formKey}_${option}`] || [];
    }


    private calculateValidFilterCount(): number {
        let count = 0;

        Object.keys(this.searchFilterForm.controls).forEach(key => {
            const group = this.searchFilterForm.get(key) as FormGroup;
            const option = group.get('option')?.value;
            const value = group.get('value')?.value;
            const hitRateMin = group.get('hitRateMin')?.value;
            const hitRateMax = group.get('hitRateMax')?.value;

            if (option === 'hitRate') {
                if (hitRateMin !== null && hitRateMax !== null &&
                    hitRateMin !== undefined && hitRateMax !== undefined &&
                    (hitRateMin !== 50 || hitRateMax !== 100)) { // Samo ako nije default
                    count++;
                }
            } else {
                if (value && value.trim() !== '') {
                    count++;
                }
            }
        });

        return count;
    }

    addFormControl() {
        const controlKey = `control-${Object.keys(this.searchFilterForm.controls).length + 1}`;
        const control = new FormGroup({
            option: new FormControl(this.allAvailableOptions![0]),
            value: new FormControl('', Validators.required),
            hitRateMin: new FormControl(50),
            hitRateMax: new FormControl(100),
            search: new FormControl('')
        });

        this.setupFormGroup(control, controlKey);
        this.searchFilterForm.addControl(controlKey, control);
    }

    onSubmit() {
        this.selectedOptions = [];

        for (const group in this.searchFilterForm.controls) {
            const rawValue = this.searchFilterForm.get(group)?.value;
            const option = rawValue?.option;
            const trimmedValue = (rawValue?.value as string)?.trim();

            let mappedOption: string;
            if (option === 'TUNING_NAME') {
                mappedOption = 'tuningName';
            } else {
                mappedOption = this.vehicleEnumToInternalKeyMap[option as VehicleDataEnum] || option;
            }

            if (option === 'hitRate') {
                if (rawValue?.hitRateMin !== null && rawValue?.hitRateMax !== null &&
                    rawValue?.hitRateMin !== undefined && rawValue?.hitRateMax !== undefined) {
                    this.selectedOptions.push({
                        option: mappedOption,
                        value: '',
                        hitRateMin: rawValue?.hitRateMin,
                        hitRateMax: rawValue?.hitRateMax
                    });
                }
            } else {
                if (trimmedValue && trimmedValue !== '') {
                    this.selectedOptions.push({
                        option: mappedOption,
                        value: trimmedValue,
                        hitRateMin: rawValue?.hitRateMin,
                        hitRateMax: rawValue?.hitRateMax
                    });
                }
            }
        }

        if (this.persistFilters) this.localStorageService.saveFilters(this.filterKey, this.selectedOptions);
        this.selectedFiltersChange.emit(this.selectedOptions);
        this.mostRequestedFilterClosed.emit(false);
        this.updateFilterCount();
    }


    deleteSearchBar(key: string) {
        this.searchFilterForm.removeControl(key);
        delete this.liveSearchSubjectMap[key];

        const updatedFilters: SearchFilterModel[] = [];
        for (const groupKey in this.searchFilterForm.controls) {
            const filterGroup = this.searchFilterForm.get(groupKey)?.value;

            let mappedOption: string;
            if (filterGroup.option === 'TUNING_NAME') {
                mappedOption = 'tuningName';
            } else {
                mappedOption = this.vehicleEnumToInternalKeyMap[filterGroup.option as VehicleDataEnum] || filterGroup.option;
            }

            if (filterGroup.option === 'hitRate') {
                if (filterGroup.hitRateMin !== null && filterGroup.hitRateMax !== null) {
                    updatedFilters.push({
                        option: mappedOption,
                        value: '',
                        hitRateMin: filterGroup.hitRateMin,
                        hitRateMax: filterGroup.hitRateMax
                    });
                }
            } else {
                const trimmedValue = (filterGroup.value as string)?.trim();
                if (trimmedValue && trimmedValue !== '') {
                    updatedFilters.push({
                        option: mappedOption,
                        value: trimmedValue,
                        hitRateMin: filterGroup.hitRateMin,
                        hitRateMax: filterGroup.hitRateMax
                    });
                }
            }
        }

        if (this.persistFilters) this.localStorageService.saveFilters(this.filterKey, updatedFilters);
        this.selectedOptions = updatedFilters;
        this.selectedFiltersChange.emit(this.selectedOptions);
        this.updateFilterCount();

        if (updatedFilters.length === 0) {
            console.log('No more filters, resetting to initial state');
            this.selectedFiltersChange.emit([]);
        }
    }


    updateFilterCount() {
        const validFilterCount = this.calculateValidFilterCount();
        this.filterCount.emit(validFilterCount);
    }

    clearFilters() {
        console.log('Clearing filters from component');

        if (this.persistFilters) this.localStorageService.clearFilters(this.filterKey);
        const controlKeys = Object.keys(this.searchFilterForm.controls);
        controlKeys.forEach(key => {
            this.searchFilterForm.removeControl(key);
            delete this.liveSearchSubjectMap[key];
        });

        this.selectedOptions = [];
        this.selectedFiltersChange.emit([]);
        this.filterCount.emit(0);
    }

    closeModal() {
        console.log('Closing modal');

        const validFilterCount = this.calculateValidFilterCount();
        this.filterCount.emit(validFilterCount);

        this.mostRequestedFilterClosed.emit(false);
    }

    hasFormControls(): boolean {
        return Object.keys(this.searchFilterForm.controls).length > 0;
    }

    ngOnDestroy() {
        Object.values(this.liveSearchSubjectMap).forEach(subject => {
            subject.unsubscribe();
        });
    }
}
